import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    sujet: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nom || !formData.email || !formData.sujet || !formData.message) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/contact-messages`, formData);
      
      if (response.status === 200) {
        toast.success('Message envoyé avec succès! Nous vous répondrons bientôt.');
        setFormData({
          nom: '',
          email: '',
          sujet: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-brand-secondary">Contactez-nous</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Nous sommes là pour répondre à toutes vos questions
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h2 className="text-3xl font-bold mb-8 text-brand-primary">Informations de Contact</h2>
            
            <div className="space-y-6">
              <Card className="p-6 border-l-4 border-brand-primary bg-white shadow-md">
                <div className="flex items-start gap-4">
                  <FaMapMarkerAlt className="text-2xl text-brand-primary mt-1" />
                  <div>
                    <h3 className="font-bold text-lg text-brand-secondary mb-2">Adresse</h3>
                    <p className="text-gray-700">
                      Abidjan, Cocody Angré<br />
                      Carrefour Prière
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-l-4 border-brand-primary bg-white shadow-md">
                <div className="flex items-start gap-4">
                  <FaPhone className="text-2xl text-brand-primary mt-1" />
                  <div>
                    <h3 className="font-bold text-lg text-brand-secondary mb-2">Téléphone</h3>
                    <p className="text-gray-700">
                      <a href="tel:0712651719" className="hover:text-brand-primary transition-colors">07 12 65 17 19</a><br />
                      <a href="tel:0100959518" className="hover:text-brand-primary transition-colors">01 00 95 95 18</a>
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-l-4 border-brand-primary bg-white shadow-md">
                <div className="flex items-start gap-4">
                  <FaEnvelope className="text-2xl text-brand-primary mt-1" />
                  <div>
                    <h3 className="font-bold text-lg text-brand-secondary mb-2">Email</h3>
                    <p className="text-gray-700">
                      <a href="mailto:contact@universitecefat.ci" className="hover:text-brand-primary transition-colors">
                        contact@universitecefat.ci
                      </a>
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-l-4 border-brand-primary bg-white shadow-md">
                <div className="flex items-start gap-4">
                  <FaClock className="text-2xl text-brand-primary mt-1" />
                  <div>
                    <h3 className="font-bold text-lg text-brand-secondary mb-2">Heures d'Ouverture</h3>
                    <p className="text-gray-700">
                      <strong>Lundi au Vendredi:</strong> 08h00 - 17h00<br />
                      <strong>Samedi:</strong> 09h00 - 14h00
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Map Placeholder */}
            <div className="mt-8 h-64 bg-gray-200 rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3972.3074920938346!2d-3.9883012!3d5.3599517!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNcKwMjEnMzUuOCJOIDPCsDU5JzE3LjkiVw!5e0!3m2!1sen!2sci!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="CEFAT-BS Location"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="p-8 shadow-xl bg-white">
              <h2 className="text-3xl font-bold mb-6 text-brand-primary">Envoyez-nous un message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="nom" className="text-brand-secondary font-semibold mb-2 block">Nom complet *</Label>
                  <Input
                    id="nom"
                    name="nom"
                    data-testid="contact-input-nom"
                    value={formData.nom}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-brand-secondary font-semibold mb-2 block">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="contact-input-email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre.email@example.com"
                    className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="sujet" className="text-brand-secondary font-semibold mb-2 block">Sujet *</Label>
                  <Input
                    id="sujet"
                    name="sujet"
                    data-testid="contact-input-sujet"
                    value={formData.sujet}
                    onChange={handleChange}
                    placeholder="Sujet de votre message"
                    className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-brand-secondary font-semibold mb-2 block">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="contact-textarea-message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Votre message..."
                    rows={6}
                    className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary resize-none"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={loading}
                  className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-6 text-lg font-semibold rounded-md transition-all hover:shadow-lg hover:-translate-y-1"
                >
                  {loading ? 'Envoi en cours...' : 'Envoyer le message'}
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}